const finiteNumber = (value, fallback) => {
  const parsed = typeof value === 'number' ? value : Number.parseFloat(String(value ?? ''));
  return Number.isFinite(parsed) ? parsed : fallback;
};

const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

const objectValue = (value) => {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value !== 'string') return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const publicImageURL = (value) => {
  const candidate = String(value || '').trim();
  return /^https?:\/\//i.test(candidate) ? candidate : '';
};

const allowedBlendModes = new Set(['normal', 'multiply', 'screen', 'overlay', 'darken', 'lighten']);
const previewTemplateCache = new Map();

const normalizeKey = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const identityMatrix = () => [1, 0, 0, 1, 0, 0];

const multiplyMatrices = (left, right) => [
  (left[0] * right[0]) + (left[2] * right[1]),
  (left[1] * right[0]) + (left[3] * right[1]),
  (left[0] * right[2]) + (left[2] * right[3]),
  (left[1] * right[2]) + (left[3] * right[3]),
  (left[0] * right[4]) + (left[2] * right[5]) + left[4],
  (left[1] * right[4]) + (left[3] * right[5]) + left[5],
];

const matrixForTransform = (name, values) => {
  switch (String(name || '').toLowerCase()) {
    case 'matrix':
      return values.length >= 6 ? values.slice(0, 6) : identityMatrix();
    case 'translate':
      return [1, 0, 0, 1, values[0] || 0, values[1] || 0];
    case 'scale': {
      const x = Number.isFinite(values[0]) ? values[0] : 1;
      const y = Number.isFinite(values[1]) ? values[1] : x;
      return [x, 0, 0, y, 0, 0];
    }
    case 'rotate': {
      const radians = ((values[0] || 0) * Math.PI) / 180;
      const rotation = [Math.cos(radians), Math.sin(radians), -Math.sin(radians), Math.cos(radians), 0, 0];
      if (values.length < 3) return rotation;
      const [cx, cy] = [values[1], values[2]];
      return multiplyMatrices(
        multiplyMatrices([1, 0, 0, 1, cx, cy], rotation),
        [1, 0, 0, 1, -cx, -cy],
      );
    }
    default:
      return identityMatrix();
  }
};

const parseTransform = (value) => {
  const expression = /([a-zA-Z]+)\s*\(([^)]*)\)/g;
  let result = identityMatrix();
  let match = expression.exec(String(value || ''));
  while (match) {
    const values = match[2].trim().split(/[\s,]+/).filter(Boolean).map(Number).filter(Number.isFinite);
    result = multiplyMatrices(result, matrixForTransform(match[1], values));
    match = expression.exec(String(value || ''));
  }
  return result;
};

const applyMatrix = (matrix, x, y) => ({
  x: (matrix[0] * x) + (matrix[2] * y) + matrix[4],
  y: (matrix[1] * x) + (matrix[3] * y) + matrix[5],
});

const templateConfig = (placement, externalVariantID) => {
  const capabilities = objectValue(placement?.capabilities);
  const template = objectValue(capabilities.preview_template);
  const files = Array.isArray(template.files) ? template.files : [];
  const matchingFile = files.find((item) => (
    Array.isArray(item?.variant_ids)
    && item.variant_ids.some((id) => String(id) === String(externalVariantID || ''))
    && publicImageURL(item?.image_url)
  ));
  const imageURL = publicImageURL(matchingFile?.image_url) || publicImageURL(template.image_url);
  if (!imageURL) return null;
  return {
    ...template,
    image_url: imageURL,
    element_id: String(template.element_id || `placeholder_${template.position || placement?.placement_key || ''}`).trim(),
  };
};

const normalizedExternalIDs = (value) => (Array.isArray(value) ? value : [])
  .map((item) => String(item || '').trim())
  .filter(Boolean);

export function placementSupportsVariant(placement, externalVariantID) {
  const externalID = String(externalVariantID || '').trim();
  if (!externalID) return true;
  const capabilities = objectValue(placement?.capabilities);
  const supported = normalizedExternalIDs(capabilities.supported_variant_ids);
  if (supported.length > 0) return supported.includes(externalID);

  const canvases = objectValue(capabilities.variant_canvases);
  const canvasVariantIDs = Object.keys(canvases);
  if (canvasVariantIDs.length > 0) return Object.prototype.hasOwnProperty.call(canvases, externalID);

  const template = objectValue(capabilities.preview_template);
  const templateVariantIDs = (Array.isArray(template.files) ? template.files : [])
    .flatMap((item) => normalizedExternalIDs(item?.variant_ids));
  return templateVariantIDs.length === 0 || templateVariantIDs.includes(externalID);
}

export function placementCanvasForVariant(placement, externalVariantID) {
  const capabilities = objectValue(placement?.capabilities);
  const canvases = objectValue(capabilities.variant_canvases);
  const selected = objectValue(canvases[String(externalVariantID || '')]);
  return {
    width: Math.max(0, Math.round(finiteNumber(selected.width, placement?.width || 0))),
    height: Math.max(0, Math.round(finiteNumber(selected.height, placement?.height || 0))),
    decorationMethod: String(selected.decoration_method || '').trim(),
  };
}

/**
 * Extracts a placement frame from Printify's catalog SVG wrapper. The wrapper
 * is the same deterministic cut/template asset used around the print canvas in
 * Printify's editor; it is not a generated product mockup.
 */
export function parseSVGPreviewTemplate(svgText, template) {
  if (typeof DOMParser === 'undefined') throw new Error('SVG preview parsing is unavailable');
  const document = new DOMParser().parseFromString(String(svgText || ''), 'image/svg+xml');
  if (document.querySelector('parsererror')) throw new Error('Provider preview template is invalid');
  const root = document.documentElement;
  const viewBox = String(root.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
  const minX = finiteNumber(viewBox[0], 0);
  const minY = finiteNumber(viewBox[1], 0);
  const viewWidth = finiteNumber(viewBox[2], finiteNumber(root.getAttribute('width'), 0));
  const viewHeight = finiteNumber(viewBox[3], finiteNumber(root.getAttribute('height'), 0));
  if (viewWidth <= 0 || viewHeight <= 0) throw new Error('Provider preview template has no view box');

  const requestedID = String(template?.element_id || '').trim();
  const requestedPosition = normalizeKey(template?.position || requestedID.replace(/^placeholder_/, ''));
  const elements = Array.from(document.querySelectorAll('[id]'));
  const target = elements.find((element) => element.getAttribute('id') === requestedID)
    || elements.find((element) => (
      String(element.getAttribute('id') || '').startsWith('placeholder_')
      && normalizeKey(String(element.getAttribute('id')).replace(/^placeholder_/, '')) === requestedPosition
    ));
  if (!target) throw new Error('Provider preview template does not contain this print area');
  const targetWidth = finiteNumber(target.getAttribute('width'), 0);
  const targetHeight = finiteNumber(target.getAttribute('height'), 0);
  if (targetWidth <= 0 || targetHeight <= 0) throw new Error('Provider print area has no dimensions');

  let matrix = identityMatrix();
  let current = target;
  while (current && current !== root) {
    const x = finiteNumber(current.getAttribute?.('x'), 0);
    const y = finiteNumber(current.getAttribute?.('y'), 0);
    const local = multiplyMatrices(parseTransform(current.getAttribute?.('transform')), [1, 0, 0, 1, x, y]);
    matrix = multiplyMatrices(local, matrix);
    current = current.parentElement;
  }
  const center = applyMatrix(matrix, targetWidth / 2, targetHeight / 2);
  const scaledWidth = targetWidth * Math.hypot(matrix[0], matrix[1]);
  const scaledHeight = targetHeight * Math.hypot(matrix[2], matrix[3]);

  return {
    image_url: publicImageURL(template?.image_url),
    image_aspect: viewWidth / viewHeight,
    canvas_aspect: targetWidth / targetHeight,
    source: String(template?.source || 'provider_svg_template'),
    frame: {
      x: (center.x - minX) / viewWidth,
      y: (center.y - minY) / viewHeight,
      width: scaledWidth / viewWidth,
      height: scaledHeight / viewHeight,
      angle: (Math.atan2(matrix[1], matrix[0]) * 180) / Math.PI,
      opacity: 0.92,
      blend_mode: 'multiply',
      border_radius: 0,
    },
  };
}

export async function loadPlacementPreviewTemplate({ placement, externalVariantID, signal, fetchImpl = fetch }) {
  const template = templateConfig(placement, externalVariantID);
  if (!template) return null;
  const cacheKey = `${template.image_url}|${template.element_id}`;
  if (!previewTemplateCache.has(cacheKey)) {
    previewTemplateCache.set(cacheKey, (async () => {
      const response = await fetchImpl(template.image_url, { signal });
      if (!response.ok) throw new Error(`Provider preview template returned ${response.status}`);
      return parseSVGPreviewTemplate(await response.text(), template);
    })());
  }
  try {
    return await previewTemplateCache.get(cacheKey);
  } catch (error) {
    previewTemplateCache.delete(cacheKey);
    throw error;
  }
}

/**
 * Normalizes optional provider/profile metadata into the stable preview shape
 * consumed by the editor. A profile is presentation-only; Product placement
 * coordinates remain relative to the real printable surface.
 */
export function productPreviewProfile({ placement, imageURL, canvasAspect = 1, resolvedProfile }) {
  const capabilities = objectValue(placement?.capabilities);
  const configured = objectValue(resolvedProfile || capabilities.preview_profile || capabilities.preview);
  const frame = objectValue(configured.frame || configured);
  const calibrated = ['x', 'y', 'width', 'height'].every((key) => Object.prototype.hasOwnProperty.call(frame, key));
  const width = clamp(finiteNumber(frame.width, 0.34), 0.08, 1.25);
  const safeAspect = clamp(finiteNumber(canvasAspect, 1), 0.1, 10);
  const derivedHeight = width / safeAspect;
  const blendMode = String(frame.blend_mode || configured.blend_mode || 'normal').toLowerCase();

  return {
    imageURL: publicImageURL(configured.image_url) || publicImageURL(imageURL),
    imageAspect: clamp(finiteNumber(configured.image_aspect, 1), 0.1, 10),
    calibrated,
    frame: {
      x: clamp(finiteNumber(frame.x, 0.5), -0.25, 1.25),
      y: clamp(finiteNumber(frame.y, 0.55), -0.25, 1.25),
      width,
      height: clamp(finiteNumber(frame.height, derivedHeight), 0.08, 1.25),
      angle: clamp(finiteNumber(frame.angle, 0), -180, 180),
      opacity: clamp(finiteNumber(frame.opacity ?? configured.opacity, 1), 0.05, 1),
      blendMode: allowedBlendModes.has(blendMode) ? blendMode : 'normal',
      borderRadius: clamp(finiteNumber(frame.border_radius, 0.02), 0, 0.5),
    },
  };
}

export function firstOfferPreviewImage(catalogDetail, offerID) {
  const match = (catalogDetail?.mockups || []).find((item) => (
    String(item?.offer_id || '') === String(offerID || '')
    && Boolean(publicImageURL(item?.preview_url) || publicImageURL(item?.source_url))
  ));
  return publicImageURL(match?.preview_url)
    || publicImageURL(match?.source_url)
    || publicImageURL(catalogDetail?.default_image_url);
}
